/**
 * @author Indeform Ltd
 * @copyright Copyright 2019, Indeform Ltd
 * @version 20220913
 * @description Handles session timeout.
 * 
 * @todo Check TODO notes.
 * @todo Add if expire/activate requests response is redirect then just logout.
 */


import ModalWindow from '../modules/modal-window/modal-window';


class SessionTimeout extends ModalWindow {

    constructor(ui) {
        super(ui,
            'modalSession',
            ['click'],
            {
                elmSP3DIframe: 'sp3dIframe',
                elmSessionTime: 'sessionTimeLeft',
                urlSessionExpiry: '/expire/',
                urlLogout: '/logout/?sessionExpired=true',
                urlLog: '/log/',
                // 60 seconds x 15 minutes
                firstCheckTime: 60 * 15,
                // 15 seconds
                timerDuration: 15,
                // 1 second
                timerRefreshTime: 1,
            }
        );

        if (this.isAttachedToElement()) {
            let component = this.ui.getComponent('SessionTimeout');

            // Only init component if it is created not reinitialized by
            // component.
            if (component === null) {
                this.init();
                this.renewSession();
            } else {
                this.renewSession();
                component.initSP3D();
            }
        }
    }


    init() {
        this._isActionMade = false;
        this._timer = this.params.timerDuration;
        this.timeout = null;
        this.timerInterval = null;
        this.elmSessionTime = document.getElementById(
            this.params.elmSessionTime);

        // Listen to click/keypress events to track user activity.
        document.addEventListener('click', this.on_document_click.bind(this));
        document.addEventListener('keypress',
            this.on_document_keypress.bind(this));

        this.initSP3D();
    }


    initSP3D() {
        this.elmSP3DIframe = document.getElementById(this.params.elmSP3DIframe);

        if (this.elmSP3DIframe) {
            this.elmSP3DIframe.addEventListener('load',
                this.on_sp3diframe_load.bind(this));
        }
    }


    addEventListenersToSP3DIframe() {
        let documentSP3D = this.elmSP3DIframe.contentDocument;

        if (documentSP3D) {
            // Listen to click events in iframe document if SP3D was loaded.
            documentSP3D.addEventListener('click',
                this.on_sp3d_click.bind(this));
        }
    }


    convertSecondsToMinutesSecond(value) {
        let minutes = Math.floor(value / 60);
        let seconds = value % 60;
        minutes = minutes < 10 ? '0' + minutes : minutes;
        seconds = seconds < 10 ? '0' + seconds : seconds;

        return minutes + ':' + seconds;
    }


    decreaseTimer() {
        if (this._timer > 0) this._timer--;
    }


    initTimeoutTimer() {
        if (this.timeout) this.timeout.terminate();

        const workerBlob = new Blob([
            `
         onmessage = (e) => {
             if (e.data[0] === 'setTimeout') {
                 setTimeout(() => {
                     postMessage(1);
                 }, e.data[1]);
             }
 
             if (e.data[0] === 'setInterval') {
                 setInterval(() => {
                     postMessage(1);
                 }, e.data[1]);
             }
         }
         `], { type: 'application/javascript' });

        const workerUrl = URL.createObjectURL(workerBlob);

        this.timeout = new Worker(workerUrl);
        this.timeout.onmessage = (e) => {
            this.getSessionExpiry(() => {
                this.setTimer(this.params.timerDuration);
                this.updateModalTimer();
                // Open session timeout modal window.
                this.open();

                if (this.timerInterval) this.timerInterval.terminate();

                this.timerInterval = new Worker(workerUrl);

                // Updates timer each second.
                this.timerInterval.onmessage = (e) => {
                    if (this.getTimer() > 0) {
                        this.updateModalTimer();
                    } else {
                        this.timerInterval.terminate();
                        this.logout();
                    }
                }
                this.timerInterval.postMessage(['setInterval', this.params.timerRefreshTime * 1000]);
            });

            this.timeout.terminate();
        }
        this.timeout.postMessage(['setTimeout', this.params.firstCheckTime * 1000]);
    }


    getIsActionMade() { return this._isActionMade; }


    async getSessionExpiry(callback) {
        let resp;

        await this.core.net.get({
            url: this.params.urlSessionExpiry,
            data: null,
            isJson: true,
        })
            .then(async (data) => {
                resp = data;

                if ('time_left' in data) {
                    if (callback !== null) callback();
                }
            })
            .catch((e) => {
                console.log(e);
            });

        if (resp === undefined) this.logout();
    }


    getTimer() { return this._timer; }


    logout() {
        window.CAN_REDIRECT_WITHOUT_PROMPT = true;
        window.location.href = this.core.net.url(this.params.urlLogout);
    }


    async renewBackendSession(callback) {
        let resp;

        await this.core.net.get({
            url: '/active/',
            data: null,
            isJson: true,
        })
            .then(async (data) => {
                resp = data;

                if (callback !== null) callback();
            })
            .catch((e) => {
                console.log(e);
            });

        if (resp === undefined) this.logout();
    }


    renewSession() {
        this.renewBackendSession(this.on_renewSession.bind(this));
    }


    reset() {
        if (this.timeout) this.timeout.terminate();
        if (this.timerInterval) this.timerInterval.terminate();
        this.resetIsActionMade();
        this.resetTimer();
    }


    resetIsActionMade() { this._isActionMade = false; }


    resetTimer() { this._timer = this.params.timerDuration; }


    setIsActionMade(value) { this._isActionMade = value; }


    setTimer(value) { this._timer = value; }


    updateModalTimer() {
        let minutesSeconds = this.convertSecondsToMinutesSecond(
            this.getTimer());

        this.elmSessionTime.textContent = minutesSeconds;
        this.decreaseTimer();
    }


    on_modalClose() {
        this.reset();
        this.renewSession();
    }


    on_modalSubmit() { this.logout(); }


    on_document_click() {
        this.initTimeoutTimer();
    }


    on_document_keypress() {
        this.initTimeoutTimer();
    }


    on_sp3d_click() {
        this.initTimeoutTimer();
    }


    on_renewSession() {
        this.reset();
        this.initTimeoutTimer();

        // Close session timeout modal window.
        this.close();
    }


    on_sp3diframe_load() { this.addEventListenersToSP3DIframe(); }
}

export { SessionTimeout }
export default SessionTimeout



//////////////////
// WEBPACK FOOTER
// ./dev_js/sso/orthosso/modules/session-timeout.js
// module id = 340
// module chunks = 0